import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);
const INACTIVITY_MS = 30 * 60 * 1000; // 30 minutes

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  // ---- Auto-logout on inactivity ----
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Forward-declare logout for use in armTimer (defined below)
  const performLogout = useCallback(async () => {
    clearTimer();
    try { await api.post("/auth/logout"); } catch {}
    // Wipe ALL client-side traces so a different user signing in next won't see prior data
    localStorage.removeItem("ayame_token");
    try { sessionStorage.clear(); } catch {}
    setUser(null);
  }, [clearTimer]);

  const armTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      performLogout();
      // Force a full reload so cached React state from previous account is fully cleared
      try { window.location.assign("/"); } catch {}
    }, INACTIVITY_MS);
  }, [clearTimer, performLogout]);

  useEffect(() => {
    (async () => {
      const hasToken = !!localStorage.getItem("ayame_token");
      if (!hasToken) { setLoading(false); return; }
      try {
        const r = await api.get("/auth/me");
        setUser(r.data);
      } catch { setUser(null); }
      setLoading(false);
    })();
  }, []);

  // Whenever a user is logged in, arm the activity timer and listen to UI events
  useEffect(() => {
    if (!user) { clearTimer(); return; }
    armTimer();
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    const reset = () => armTimer();
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    return () => {
      events.forEach(e => window.removeEventListener(e, reset));
      clearTimer();
    };
  }, [user, armTimer, clearTimer]);

  const login = async (identifier, password) => {
    // Wipe any prior session state to prevent account-mixing
    localStorage.removeItem("ayame_token");
    try { sessionStorage.clear(); } catch {}
    const r = await api.post("/auth/login", { identifier, password });
    if (r.data.token) localStorage.setItem("ayame_token", r.data.token);
    setUser(r.data);
    return r.data;
  };
  const signup = async (data) => {
    localStorage.removeItem("ayame_token");
    try { sessionStorage.clear(); } catch {}
    const r = await api.post("/auth/signup", data);
    if (r.data.token) localStorage.setItem("ayame_token", r.data.token);
    setUser(r.data);
    return r.data;
  };
  const logout = performLogout;
  const refreshMe = async () => {
    try { const r = await api.get("/auth/me"); setUser(r.data); return r.data; }
    catch { return null; }
  };

  return <AuthCtx.Provider value={{ user, loading, login, signup, logout, setUser, refreshMe }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

export const dashboardForRole = (role) => {
  switch (role) {
    case "admin": return "/admin";
    case "owner": return "/admin-owner";
    case "receptionist": return "/reception";
    case "agent": return "/staff";
    case "customer": return "/account";
    default: return "/";
  }
};

export const loginForRole = (role) => {
  switch (role) {
    case "admin": return "/admin/login";
    case "owner": return "/admin-owner/login";
    case "receptionist": return "/reception/login";
    case "agent": return "/staff/login";
    default: return "/login";
  }
};
